import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../components/common/PageHeader';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaBuilding } from 'react-icons/fa';

const ContactUsPage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Contact Us' }
  ];

  const offices = [
    {
      icon: <FaBuilding className="w-6 h-6 text-secondary" />,
      title: "UK Office",
      address: [
        "Colmore Building",
        "20 Colmore Circus Queensway",
        "Birmingham, England",
        "B4 6AT"
      ],
      phone: "+44 7468 903587",
      email: null
    },
    {
      icon: <FaBuilding className="w-6 h-6 text-secondary" />,
      title: "Cayman Office",
      address: [
        "PO Box 1093, Boundary Hall",
        "Cricket Square",
        "Grand Cayman",
        "KY1-1102, Cayman Islands"
      ],
      phone: null,
      email: "info@segwaycapital.com"
    }
  ];

  return (
    <>
      <PageHeader 
        title="Contact Us" 
        breadcrumbs={breadcrumbs}
        backgroundImage="/assets/smiling-woman-showing-male-colleague-tablet-screen-cafe-outdoors.jpg"
      />
      
      <section className="py-8 md:py-24 bg-background">
        <div className="container-custom">
          {/* Office Locations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            {offices.map((office, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-custom p-8"
              >
                <div className="flex items-center mb-6">
                  <div className="bg-secondary/10 p-3 rounded-full mr-4">
                    {office.icon}
                  </div>
                  <h3 className="text-2xl font-heading font-semibold text-primary">{office.title}</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <FaMapMarkerAlt className="text-secondary mt-1 mr-3 flex-shrink-0" />
                    <div className="text-gray-600">
                      {office.address.map((line, idx) => (
                        <div key={idx}>{line}</div>
                      ))}
                    </div>
                  </div>
                  
                  {office.phone && (
                    <div className="flex items-center">
                      <FaPhone className="text-secondary mr-3" />
                      <span className="text-gray-600">{office.phone}</span>
                    </div>
                  )}
                  
                  {office.email && (
                    <div className="flex items-center">
                      <FaEnvelope className="text-secondary mr-3" />
                      <a 
                        href={`mailto:${office.email}`}
                        className="text-gray-600 hover:text-secondary transition-colors duration-300"
                      >
                        {office.email}
                      </a>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="lg:col-span-2 space-y-6"
            >
              <h2 className="text-3xl font-heading font-bold text-primary">Get In Touch</h2>
              <p className="text-gray-600">
                Have questions about our investment services or want to learn more about how we can help you achieve your financial goals? Reach out to us and one of our experts will get back to you shortly.
              </p>
              <div className="bg-primary text-white p-6 rounded-xl">
                <h3 className="text-xl font-semibold mb-3">Contact Information</h3>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-semibold text-secondary mb-1">UK Office</h4>
                    <p className="text-sm text-gray-200">Phone: +44 7468 903587</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary mb-1">Cayman Office</h4>
                    <p className="text-sm text-gray-200">Email: info@segwaycapital.com</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-xl font-semibold mb-3 text-primary">Important Notice</h3>
                <p className="text-sm text-gray-600">
                  <span className="font-semibold text-secondary">S Capital Fund, (Cayman) Limited</span> is authorised to conduct Securities Investment Business by the Cayman Islands Monetary Authority (CIMA), license number <span className="font-semibold text-secondary">1609101</span>.
                </p>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="lg:col-span-3 bg-white rounded-xl shadow-custom p-8"
            >
              <h3 className="text-2xl font-heading font-semibold mb-6 text-primary">Send us a Message</h3>
              <form className="space-y-6" action='/apply-now'>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                    <input
                      type="text"
                      id="name"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-secondary focus:border-transparent"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-secondary focus:border-transparent"
                      placeholder="Enter your email address"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-secondary focus:border-transparent"
                    placeholder="How can we help you?"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <textarea
                    id="message"
                    rows="5"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-secondary focus:border-transparent"
                    placeholder="Enter your message here..."
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full py-3 px-6 bg-secondary hover:bg-secondary-dark text-white font-medium rounded-lg transition-colors btn-3d"
                >
                  Send Message
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactUsPage; 